import { useCallback } from "react";
import { container } from "tsyringe";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import Button, { EButtonVariant, EButtonColor } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography from "components/elements/Typography";
import Form, { useForm } from "components/materials/Form";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import Input from "components/materials/Form/InputElement";
import I18n from "components/materials/I18n";
import ContributorRequestResource from "common/resources/Project/Config/Contributor/ContributorRequestResource";
import ContributorResponseResource from "common/resources/Project/Config/Contributor/ContributorResponseResource";
import ProjectTeamCastRequestResource from "common/resources/Project/Config/Team/Cast/ProjectTeamCastRequestResource";
import ProjectTeamCastResponseResource from "common/resources/Project/Config/Team/Cast/ProjectTeamCastResponseResource";
import { useProjectTeamContext } from "components/pages/ProfessionalSpace/contexts/ProjectTeam";
import { useProjectId } from "hooks/useProjectId";
import ContributorService from "services/projects/ContributorService";
import CastService from "services/projects/CastService";
import classes from "./classes.module.scss";

type IProps = {
	isOpen: boolean;
	onClose: () => void;
	onCreated: (cast: ProjectTeamCastResponseResource) => void;
};

const castService = container.resolve(CastService);
const contributorService = container.resolve(ContributorService);

export default function CreateCastingModal(props: IProps) {
	const { formRef, errors, handleError, clearErrors } = useForm();
	const { projectId } = useProjectId();
	const { projectTeamId } = useProjectTeamContext();

	const createContributor = useCallback(
		async (formData: { [key: string]: unknown }) => {
			const contributorRequestResource = ContributorRequestResource.hydrate<ContributorRequestResource>({
				firstName: formData["firstName"] as string,
				lastName: formData["lastName"] as string,
				companyName: formData["companyName"] as string,
			});

			return contributorRequestResource.validateOrReject().then(() => contributorService.create(projectId, projectTeamId, contributorRequestResource));
		},
		[projectId, projectTeamId],
	);

	const createCast = useCallback(
		async (formData: { [key: string]: unknown }, contributor: ContributorResponseResource) => {
			const castRequestResource = ProjectTeamCastRequestResource.hydrate<ProjectTeamCastRequestResource>({
				characterName: [{ lang: I18n.getLang(), text: formData["characterName"] as string }],
				actorId: contributor.id,
				projectTeamId,
			});

			const image = (formData["image"] as FileList)?.[0];

			return castRequestResource.validateOrReject().then(() => castService.create(projectId, projectTeamId, castRequestResource, image));
		},
		[projectId, projectTeamId],
	);

	const onCreateCasting = useCallback(
		(formData: { [key: string]: unknown }) => {
			clearErrors();

			createContributor(formData)
				.then((contributor) => {
					createCast(formData, contributor).then(props.onCreated).catch(handleError);
				})
				.catch(handleError);
		},
		[clearErrors, createCast, createContributor, handleError, props.onCreated],
	);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.casting.create_casting_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form onSubmit={onCreateCasting} errors={errors} formRef={formRef} className={classes["form-container"]}>
				<div className={classes["inputs-container"]}>
					<Input name="characterName" label={I18n.asset.pages.professional_space.tabs.casting.create_casting_modal.name} className={classes["input"]} />

					<div className={classes["group"]}>
						<Input name="firstName" label={I18n.asset.pages.professional_space.tabs.casting.create_casting_modal.first_name} className={classes["input"]} />
						<Input name="lastName" label={I18n.asset.pages.professional_space.tabs.casting.create_casting_modal.last_name} className={classes["input"]} />
					</div>

					<DragAndDropFile
						name="image"
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.casting.create_casting_modal.drag_and_drop.image}
						description={I18n.asset.pages.professional_space.tabs.casting.create_casting_modal.drag_and_drop.description}
						formRef={formRef}
						className={classes["drag-and-drop"]}
					/>
				</div>

				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose}>
						{I18n.asset.pages.professional_space.tabs.casting.create_casting_modal.cancel}
					</Button>
					<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY}>
						{I18n.asset.pages.professional_space.tabs.casting.create_casting_modal.create_casting}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}
